import React, { useRef } from 'react';
import Modal from 'cccisd-modal';
import notification from 'cccisd-notification';
import axios from 'cccisd-axios';
import { Formik, Form, Field, CccisdInput, CccisdDatepicker } from 'cccisd-formik';
import moment from 'moment';
import style from './style.css';

const Boilerplate = window.cccisd.boilerplate;

const PrePostLinks = props => {
    const { sites = [], org = null, assignmentId = null, surveyHandle = null, onSuccess } = props;
    const modalRef = useRef(null);

    const updateSelectedSites = (selectedSites, groupId, isChecked) => {
        if (isChecked) {
            return selectedSites.includes(groupId) ? selectedSites : [...selectedSites, groupId];
        }

        return selectedSites.filter(selectedGroupId => selectedGroupId !== groupId);
    };

    const getParams = (type = 'pre', values, siteId, preDeploymentId = null) => {
        const siteName = sites.find(s => s.group.groupId === siteId)?.group.label || '';
        const hash = Math.random().toString(36).substring(2, 9);
        const opensAt = type === 'pre' ? values.preOpensOn : values.postOpensOn;
        const closesAt = type === 'pre' ? values.preClosesOn : values.postClosesOn;

        return {
            type: 'anon',
            roleHandle: 'respondent',
            hash,
            endpoint: `${hash}${type}${siteId}`,
            handle: `${type}_${siteId}_${hash}`,
            label: `${type} - ${values.collectionDetails}`,
            description: `org_${org?.group?.groupId || ''}`,
            isOpen: false,
            opensAt: opensAt ? moment(opensAt).utc().toString() : '',
            closesAt: closesAt ? moment(closesAt).utc().toString() : '',
            respondentsFromDcwId: null, // need to update store to save this
            settings: {
                siteId,
                siteName,
                orgId: org?.group?.groupId || null,
                orgName: org?.group?.label || '',
                respondentConfig: 'anonymous',
                type,
                preDeploymentId: type === 'post' ? preDeploymentId : null,
                allowRetake: false,
                autoLogout: true,
                autoLogoutTime: 20,
                anonAutoLogoutMessage: `<p>Inactivity Timeout</p><p>We closed your survey session after a period of inactivity.</p>\n                    <p>Sorry! <a href="https://commonjustice.test/d/${hash}" target="_blank" rel="noreferrer noopener">Start a new session</a></p>`,
                branchedCompletionConfig: {},
                chainEmailFromPawn: '',
                chainPhoneFromPawn: '',
                chainLanguageFromPawn: '',
                closeOnTarget: false,
                closedMessage: 'This survey is no longer accepting responses.',
                closedRoster: false,
                completedMessage: 'You have already completed this survey. Thank you!',
                completionMessage: 'Thank you! You have completed the survey.',
                defaultOpenClose: {},
                individualPasscode: false,
                isAssignmentPlanEligible: false,
                isRepeated: false,
                knownAutoLogoutMessage: `<p>Inactivity Timeout</p><p>We closed your survey session after a period of inactivity. Sorry!</p>\n                        <p>You can resume your session from the link you were given, or contact your survey administrator for further assistance.</p>`,
                logoutOnCompletion: false,
                metricToRespondentPropertyMappings: [],
                options: [surveyHandle],
                otherAllowedRoles: ['uberadmin', 'questprojectadmin'],
                passcodeBackground: 'solidColor',
                passcodeBackgroundColor: '#F1F0F5',
                passcodeCompletionButtonText: 'Ok',
                passcodeLabel: 'Your Passcode',
                postCompletionAction: 'message',
                recaptcha: false,
                repeatInterval: '',
                requireLogin: false,
                requirePass: false,
                showReviewButtonOnCompletion: false,
                useEndpoint: true,
            },
            assignmentId,
        };
    };

    const handleSubmit = async values => {
        try {
            if (!Array.isArray(values.selectedSites) || values.selectedSites.length === 0) {
                throw new Error('Select one or more sites before continuing.');
            }

            if (!assignmentId || !org?.group?.groupId) {
                throw new Error('Missing required organization/assignment context. Please reload and try again.');
            }

            await Promise.all(
                values.selectedSites.map(async site => {
                    const preParams = getParams('pre', values, site);
                    const responsePre = await axios.post(
                        Boilerplate.route('api.assignmentDeployment.store'),
                        preParams
                    );

                    if (responsePre.data.errors) {
                        throw responsePre.data.errors;
                    }

                    const preDeploymentId = responsePre.data.id;
                    const postParams = getParams('post', values, site, preDeploymentId);
                    const responsePost = await axios.post(
                        Boilerplate.route('api.assignmentDeployment.store'),
                        postParams
                    );

                    if (responsePost.data.errors) {
                        throw responsePost.data.errors;
                    }
                })
            );

            if (modalRef.current) {
                modalRef.current.close();
            }

            if (onSuccess) {
                await onSuccess();
            }

            notification({
                message: 'Pre/Post links created successfully.',
                type: 'success',
            });
        } catch (error) {
            let errorMessage = 'An unexpected error occurred.';

            if (error instanceof Error) {
                errorMessage = error.message;
            } else if (Array.isArray(error)) {
                errorMessage = error.join(', ');
            } else if (error && typeof error === 'object') {
                errorMessage = Object.values(error).join(', ');
            }

            notification({ message: errorMessage, type: 'danger' });
            throw error;
        }

        return values;
    };

    const validate = values => {
        let errors = {};

        if (!values.collectionDetails) {
            errors.collectionDetails = 'Collection Details is required';
        }

        if (!values.preOpensOn) {
            errors.preOpensOn = 'Pre- Opens On date is required';
        }

        if (values.preOpensOn && new Date(values.preOpensOn) < new Date()) {
            errors.preOpensOn = 'Pre- Opens On date cannot be in the past.';
        }

        if (values.preClosesOn && new Date(values.preClosesOn) < new Date()) {
            errors.preClosesOn = 'Pre- Closes On date cannot be in the past.';
        }

        if (values.preClosesOn && values.preOpensOn === values.preClosesOn) {
            errors.preOpensOn = 'Pre- Opens On date and Pre- Closes On date cannot be the same.';
            errors.preClosesOn = 'Pre- Opens On date and Pre- Closes On date cannot be the same.';
        }

        if (values.preOpensOn && values.preClosesOn && new Date(values.preClosesOn) < new Date(values.preOpensOn)) {
            errors.preClosesOn = 'Pre- Closes On date cannot be before Pre- Opens On date.';
        }

        if (!values.postOpensOn) {
            errors.postOpensOn = 'Post- Opens On date is required';
        }

        if (values.postOpensOn && new Date(values.postOpensOn) < new Date()) {
            errors.postOpensOn = 'Post- Opens On date cannot be in the past.';
        }

        if (values.preOpensOn && values.postOpensOn && new Date(values.postOpensOn) < new Date(values.preOpensOn)) {
            errors.postOpensOn = 'Post- Opens On date cannot be before Pre- Opens On date.';
        }

        if (values.postClosesOn && new Date(values.postClosesOn) < new Date()) {
            errors.postClosesOn = 'Post- Closes On date cannot be in the past.';
        }

        if (
            values.postOpensOn &&
            values.postClosesOn &&
            new Date(values.postClosesOn) <= new Date(values.postOpensOn)
        ) {
            errors.postClosesOn = 'Post- Closes On date cannot be the same as or before Post- Opens On date.';
        }

        if (!Array.isArray(values.selectedSites) || values.selectedSites.length === 0) {
            errors.selectedSites = 'Select one or more sites before continuing.';
        }

        return errors;
    };

    return (
        <Modal
            ref={modalRef}
            title="Create Pre-/Post- Survey Links"
            trigger={
                <button type="button" className="btn btn-primary">
                    Create Pre-/Post- Survey Links
                </button>
            }
        >
            <Formik
                initialValues={{
                    collectionDetails: '',
                    preOpensOn: '',
                    preClosesOn: '',
                    postOpensOn: '',
                    postClosesOn: '',
                    selectedSites: [],
                }}
                onSubmit={handleSubmit}
                validate={validate}
                render={({ values, setFieldValue, errors, submitCount }) => (
                    <Form>
                        <Field
                            name="collectionDetails"
                            component={CccisdInput}
                            label={<span className={style.required}>Collection Details</span>}
                            type="text"
                        />

                        <div className="row">
                            <div className="col-sm-6">
                                <Field
                                    name="preOpensOn"
                                    component={CccisdDatepicker}
                                    label={<span className={style.required}>Pre- Opens On</span>}
                                    placeholder="Pick date & time"
                                    showTimeSelect
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    name="preClosesOn"
                                    component={CccisdDatepicker}
                                    label="Pre- Closes On"
                                    placeholder="Pick date & time"
                                    showTimeSelect
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-sm-6">
                                <Field
                                    name="postOpensOn"
                                    component={CccisdDatepicker}
                                    label={<span className={style.required}>Post- Opens On</span>}
                                    placeholder="Pick date & time"
                                    showTimeSelect
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    name="postClosesOn"
                                    component={CccisdDatepicker}
                                    label="Post- Closes On"
                                    placeholder="Pick date & time"
                                    showTimeSelect
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '15px' }}>
                            <label>
                                <strong>
                                    <span className={style.required}>Select Site(s)</span>
                                </strong>
                            </label>
                            <table className="table table-bordered table-striped" style={{ marginBottom: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '70px' }}>Select</th>
                                        <th>Site Name</th>
                                        <th>Site Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sites.length > 0 ? (
                                        sites.map(site => (
                                            <tr key={site.group.groupId}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={values.selectedSites.includes(site.group.groupId)}
                                                        onChange={event =>
                                                            setFieldValue(
                                                                'selectedSites',
                                                                updateSelectedSites(
                                                                    values.selectedSites,
                                                                    site.group.groupId,
                                                                    event.target.checked
                                                                )
                                                            )
                                                        }
                                                    />
                                                </td>
                                                <td>{site.group.label}</td>
                                                <td>{site.fields.siteType}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="3">No sites available.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {submitCount > 0 && errors.selectedSites && (
                                <div className="text-danger" style={{ marginTop: '8px' }}>
                                    {errors.selectedSites}
                                </div>
                            )}
                        </div>

                        <div className="d-flex justify-content-between">
                            <button
                                className="btn btn-secondary"
                                type="button"
                                onClick={() => modalRef.current && modalRef.current.close()}
                            >
                                Cancel
                            </button>
                            <button className="btn btn-primary" type="submit">
                                Submit
                            </button>
                        </div>
                    </Form>
                )}
            />
        </Modal>
    );
};

export default PrePostLinks;
